import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { formatApiError } from '../lib/errors';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Plus, X, Save, Upload, BookOpen, FolderTree, MapPin, Store, Building2 } from 'lucide-react';
import LocationField from '../components/LocationField';
import MapPointPicker from '../components/MapPointPicker';
import CustomFieldsRenderer from '../components/CustomFieldsRenderer';
import AiListingAssistant from '../components/AiListingAssistant';
import { resolveAssetUrl } from '../lib/assets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const DRAFT_KEY = 'kolv_listing_draft_v1';

const EMPTY_FORM_DATA = {
  title: '', description: '', category: '', subcategory: '', product_type: '',
  business_type: '', rental_type: '', region: '', price: '', price_type: 'fixed',
  images: [], contact_phone: '', contact_email: '', website: '', address: '',
  event_date: '', custom_subcategory: '', custom_product_type: '', custom_business_type: '',
  age_restriction: '', event_subcategory: '', event_type: '', service_subcategory: '',
  service_type: '', catalogs: [], collection_ids: [], service_area_type: '',
  service_area_radius: '', service_area_cities: [], service_area_region: '',
  available_quantity: '', is_paused: false, location_id: '', location_name: '',
  location_district: '', location_district_slug: '', lat: null, lng: null, vat_rate: '',
};

// Persist an in-progress NEW listing so an accidental reload / tab close doesn't
// wipe what the seller typed. Edit mode never uses drafts (loads from server).
const loadListingDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function CreateListingPage() {
  const { id } = useParams(); // For edit mode
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState(() => {
    const d = !isEditMode ? loadListingDraft() : null;
    return d?.formData ? { ...EMPTY_FORM_DATA, ...d.formData } : { ...EMPTY_FORM_DATA };
  });
  const [customFields, setCustomFields] = useState(() => {
    const d = !isEditMode ? loadListingDraft() : null;
    return d?.customFields || {};
  });
  const [fieldSchema, setFieldSchema] = useState([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [imageLimit, setImageLimit] = useState(5);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [regions, setRegions] = useState([]);
  const [regionNames, setRegionNames] = useState({});
  // Granular sub-location (OSM-imported villages/districts) autocomplete state
  const [locationQuery, setLocationQuery] = useState(() => {
    const d = !isEditMode ? loadListingDraft() : null;
    return d?.locationQuery || '';
  });
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [catalogInfo, setCatalogInfo] = useState(null);
  const [paidCatalogs, setPaidCatalogs] = useState([]); // catalog ids the seller can use
  const [catalogVerified, setCatalogVerified] = useState(false);
  const [shopCollections, setShopCollections] = useState([]); // seller's shop collections
  const [taxonomy, setTaxonomy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userRegion, setUserRegion] = useState('');
  const [draftRestored, setDraftRestored] = useState(() => !isEditMode && !!loadListingDraft());

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchRegions();
    fetchCatalogInfo();
    fetchPaidCatalogs();
    fetchShopCollections();
    fetchUserRegion();
    fetchTaxonomy();
    fetchImageLimit();
    if (isEditMode) {
      fetchListing();
    }
  }, [user, id]);

  const fetchImageLimit = async () => {
    try {
      const { data } = await axios.get(`${API}/premium/status`);
      setImageLimit(data.active ? (data.image_limit || 5) : 5);
    } catch (e) { setImageLimit(5); }
  };

  const [shopDefaults, setShopDefaults] = useState({});  // {working_hours, languages} from shop profile
  const [shopLocation, setShopLocation] = useState(null); // full location inherited from shop profile

  const fetchUserRegion = async () => {
    try {
      const { data } = await axios.get(`${API}/shop-profile`);
      const shopRegion = data?.region || user?.region || '';
      setUserRegion(shopRegion);
      const loc = {
        region: data?.region || '',
        location_id: data?.location_id || '',
        location_name: data?.location_name || '',
        location_region_slug: data?.location_region_slug || '',
        location_district: data?.location_district || '',
        location_district_slug: data?.location_district_slug || '',
        lat: data?.lat ?? null,
        lng: data?.lng ?? null,
      };
      setShopLocation(loc);
      if (!isEditMode) {
        setFormData(prev => ({
          ...prev,
          region: prev.region || shopRegion,
          location_id: loc.location_id || prev.location_id,
          location_name: loc.location_name || prev.location_name,
          location_district: loc.location_district || prev.location_district,
          location_district_slug: loc.location_district_slug || prev.location_district_slug,
          lat: loc.lat ?? prev.lat,
          lng: loc.lng ?? prev.lng,
        }));
      }
      // Remember working_hours / languages from shop profile so the dynamic
      // form can prefill those keys in custom_fields when the chosen schema
      // includes them.
      setShopDefaults({
        working_hours: data?.shop_working_hours || '',
        languages: Array.isArray(data?.shop_languages) ? data.shop_languages : [],
      });
    } catch (e) {
      if (user?.region) {
        setUserRegion(user.region);
        if (!isEditMode) setFormData(prev => prev.region ? prev : { ...prev, region: user.region });
      }
    }
  };

  const fetchRegions = async () => {
    try {
      const { data } = await axios.get(`${API}/regions`);
      setRegions(data.regions);
      setRegionNames(data.names);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  // Debounced location autocomplete: fetch up to 15 OSM-imported villages /
  // districts matching the typed prefix, scoped to the chosen region for
  // relevance. Triggers when query >= 2 chars.
  useEffect(() => {
    const activeRegion = formData.region || userRegion;
    if (!locationQuery || locationQuery.trim().length < 2 || !activeRegion) {
      setLocationSuggestions([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/locations`, {
          params: { q: locationQuery.trim(), region: activeRegion, limit: 15 },
        });
        setLocationSuggestions(data.locations || []);
      } catch (e) {
        setLocationSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [locationQuery, formData.region, userRegion]);

  const pickLocation = (loc) => {
    setFormData(prev => ({ ...prev, location_id: loc.id, location_name: loc.name }));
    setLocationQuery(loc.name);
    setLocationDropdownOpen(false);
  };

  const clearLocation = () => {
    setFormData(prev => ({ ...prev, location_id: '', location_name: '' }));
    setLocationQuery('');
  };

  // Address sources for a NEW listing — inherited from the seller's shop
  // profile and/or each business page that has an address. The listing adopts
  // one source's address by default; the seller can still edit it per listing
  // via the unified LocationField (edits never change the source).
  const TEMPLATE_LABEL = { reservations: 'Rezervācijas', menu: 'Ēdienkarte', business_card: 'Vizītkarte' };
  const [addressSources, setAddressSources] = useState([]);
  const [activeSourceId, setActiveSourceId] = useState('shop');

  const applySource = async (src) => {
    setActiveSourceId(src.key);
    if (src.kind === 'shop') {
      setFormData(prev => ({
        ...prev,
        region: src.region || prev.region,
        location_id: src.location_id || '',
        location_name: src.location_name || '',
        location_region_slug: src.location_region_slug || '',
        location_district: src.location_district || '',
        location_district_slug: src.location_district_slug || '',
        lat: src.lat ?? prev.lat,
        lng: src.lng ?? prev.lng,
      }));
      return;
    }
    // Business page: resolve its free-text address + coords into a region.
    let region = '', regionSlug = '', cityId = '';
    try {
      if (src.lat != null && src.lng != null) {
        const { data } = await axios.get(`${API}/locations/nearest-city`, { params: { lat: src.lat, lng: src.lng } });
        region = data.city_slug || '';
        regionSlug = data.region_slug || '';
        cityId = data.id || '';
      }
    } catch (e) { /* best-effort region resolve */ }
    setFormData(prev => ({
      ...prev,
      region: region || prev.region,
      location_id: cityId,
      location_name: src.address || src.label || '',
      location_region_slug: regionSlug,
      location_district: '',
      location_district_slug: '',
      lat: src.lat ?? prev.lat,
      lng: src.lng ?? prev.lng,
    }));
  };

  // Compose the address-source list once the shop profile has loaded.
  useEffect(() => {
    if (isEditMode) return;
    let cancelled = false;
    (async () => {
      const sources = [];
      if (shopLocation && (shopLocation.region || shopLocation.location_name)) {
        sources.push({ key: 'shop', kind: 'shop', label: language === 'lv' ? 'Veikals' : 'Shop', ...shopLocation });
      }
      try {
        const { data } = await axios.get(`${API}/business-page/me/pages`);
        (data.pages || []).forEach((p, i) => {
          const bc = p.business_card || {};
          const address = p.address || bc.address || null;
          const lat = p.lat ?? bc.lat ?? null;
          const lng = p.lng ?? bc.lng ?? null;
          if (lat != null && lng != null) {
            sources.push({
              key: `page-${p.id}`, kind: 'page',
              label: p.display_name || TEMPLATE_LABEL[p.template] || `Lapa ${i + 1}`,
              address, lat, lng,
            });
          }
        });
      } catch (e) { /* seller has no business pages */ }
      if (cancelled) return;
      setAddressSources(sources);
      // No shop address but a page has one → adopt the first page by default.
      if (sources.length && !sources.some(s => s.key === 'shop')) {
        applySource(sources[0]);
      }
    })();
    return () => { cancelled = true; };
  }, [shopLocation, isEditMode, language]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save the NEW-listing draft on every change so an accidental reload /
  // tab close doesn't wipe what the seller typed. Cleared on successful submit.
  useEffect(() => {
    if (isEditMode) return;
    const hasContent = formData.title || formData.description || formData.category
      || (formData.images && formData.images.length) || Object.keys(customFields).length;
    try {
      if (hasContent) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ formData, customFields, locationQuery }));
      }
    } catch { /* localStorage unavailable — drafts are best-effort */ }
  }, [formData, customFields, locationQuery, isEditMode]);

  const clearListingDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
  };

  const discardDraft = () => {
    clearListingDraft();
    setFormData({ ...EMPTY_FORM_DATA, region: userRegion || '' });
    setCustomFields({});
    setLocationQuery('');
    setDraftRestored(false);
  };

  // Re-fetch the category-specific field schema whenever the taxonomy path changes.
  useEffect(() => {
    const cat = formData.category;
    if (!cat) { setFieldSchema([]); return; }
    // Choose the most relevant sub/type triple for the selected category:
    //  - event: event_subcategory + event_type
    //  - service: service_subcategory + service_type
    //  - shop / business / project: subcategory + product_type (or business_type)
    let sub = '';
    let typ = '';
    if (cat === 'event') {
      sub = formData.event_subcategory;
      typ = formData.event_type;
    } else if (cat === 'service') {
      sub = formData.service_subcategory;
      typ = formData.service_type;
    } else if (cat === 'shop') {
      sub = formData.subcategory;
      typ = formData.product_type;
    } else if (cat === 'business') {
      sub = formData.business_type;
    }
    const params = new URLSearchParams({ category: cat });
    if (sub) params.set('subcategory', sub);
    if (typ) params.set('type', typ);
    axios.get(`${API}/field-schema?${params.toString()}`)
      .then(({ data }) => {
        const schema = data.fields || [];
        setFieldSchema(schema);
        // Prefill working_hours and languages from shop profile if the schema
        // has those keys and the value isn't already set. Respect the user's
        // subsequent edits — we only fill blanks.
        setCustomFields((prev) => {
          const next = { ...prev };
          for (const f of schema) {
            if (f.key === 'working_hours' && !next.working_hours && shopDefaults.working_hours) {
              next.working_hours = shopDefaults.working_hours;
            }
            if (f.key === 'languages' && (!next.languages || next.languages.length === 0) && (shopDefaults.languages || []).length > 0) {
              next.languages = [...shopDefaults.languages];
            }
          }
          return next;
        });
      })
      .catch(() => setFieldSchema([]));
  }, [
    formData.category, formData.subcategory, formData.product_type,
    formData.business_type, formData.event_subcategory, formData.event_type,
    formData.service_subcategory, formData.service_type,
    shopDefaults.working_hours, shopDefaults.languages,
  ]);

  const fetchCatalogInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/catalog/info`);
      setCatalogInfo(data);
    } catch (error) {
      console.error('Error fetching catalog info:', error);
    }
  };

  const fetchPaidCatalogs = async () => {
    try {
      // Catalogs are FREE for verified shops — a verified shop can place a
      // listing in ANY catalog directly from this form.
      const { data } = await axios.get(`${API}/catalog/my-access`);
      setPaidCatalogs(data.catalogs || []);
      setCatalogVerified(!!data.is_verified);
    } catch (error) {
      console.error('Error fetching catalog access:', error);
      setPaidCatalogs([]);
      setCatalogVerified(false);
    }
  };

  const fetchShopCollections = async () => {
    try {
      if (!user) return;
      const uid = user._id || user.id;
      const { data } = await axios.get(`${API}/shops/${uid}/collections`);
      setShopCollections(data || []);
    } catch (error) {
      console.error('Error fetching shop collections:', error);
      setShopCollections([]);
    }
  };

  const createCollectionInline = async (name) => {
    if (!name || name.trim().length < 2) return null;
    try {
      const { data } = await axios.post(`${API}/shop-collections`, { name: name.trim() });
      setShopCollections(prev => [...prev, data]);
      return data;
    } catch (e) {
      alert(formatApiError(e, language === 'lv' ? 'Kļūda' : 'Error'));
      return null;
    }
  };

  const fetchTaxonomy = async () => {
    try {
      const { data } = await axios.get(`${API}/categories`);
      setTaxonomy(data.taxonomy);
    } catch (error) {
      console.error('Error fetching taxonomy:', error);
    }
  };

  const fetchListing = async () => {
    try {
      const { data } = await axios.get(`${API}/listings/${id}`);
      setFormData({
        title: data.title || '',
        description: data.description || '',
        category: data.category || '',
        subcategory: data.subcategory || '',
        product_type: data.product_type || '',
        business_type: data.business_type || '',
        rental_type: data.rental_type || '',
        region: data.region || '',
        price: data.price?.toString() || '',
        price_type: data.price_type || 'fixed',
        images: data.images || [],
        contact_phone: data.contact_phone || '',
        contact_email: data.contact_email || '',
        website: data.website || '',
        address: data.address || '',
        event_date: data.event_date ? new Date(data.event_date).toISOString().slice(0, 16) : '',
        custom_subcategory: data.custom_subcategory || '',
        custom_product_type: data.custom_product_type || '',
        custom_business_type: data.custom_business_type || '',
        age_restriction: data.age_restriction || '',
        event_subcategory: data.event_subcategory || '',
        event_type: data.event_type || '',
        service_subcategory: data.service_subcategory || '',
        service_type: data.service_type || '',
        catalogs: data.catalogs || [],
        collection_ids: data.collection_ids || [],
        service_area_type: data.service_area_type || '',
        service_area_radius: data.service_area_radius || '',
        service_area_cities: data.service_area_cities || [],
        service_area_region: data.service_area_region || '',
        available_quantity: data.available_quantity === null || data.available_quantity === undefined ? '' : String(data.available_quantity),
        vat_rate: data.vat_rate === null || data.vat_rate === undefined ? '' : String(data.vat_rate),
        is_paused: !!data.is_paused,
        location_id: data.location_id || '',
        location_name: data.location_name || '',
        location_region_slug: data.location_region_slug || '',
        location_district: data.location_district || '',
        location_district_slug: data.location_district_slug || '',
        lat: data.lat ?? null,
        lng: data.lng ?? null
      });
      // Seed autocomplete input with the existing location name so the seller
      // sees their saved choice instead of an empty box.
      if (data.location_name) {
        setLocationQuery(data.location_name);
      }
      setCustomFields(data.custom_fields || {});
    } catch (error) {
      console.error('Error fetching listing:', error);
      navigate('/my-listings');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddImage = () => {
    if (formData.images.length >= imageLimit) {
      setError(language === 'lv' ? `Tavs plāns atļauj līdz ${imageLimit} bildēm. Jaunini plānu vairāk bilžu pievienošanai.` : `Your plan allows up to ${imageLimit} images.`);
      return;
    }
    if (newImageUrl.trim() && !formData.images.includes(newImageUrl.trim())) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImageUrl.trim()]
      }));
      setNewImageUrl('');
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (formData.images.length >= imageLimit) {
      setError(language === 'lv' ? `Tavs plāns atļauj līdz ${imageLimit} bildēm. Jaunini plānu vairāk bilžu pievienošanai.` : `Your plan allows up to ${imageLimit} images.`);
      e.target.value = '';
      return;
    }
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) {
      setError(language === 'lv' ? 'Fails pārāk liels (max 5MB)' : 'File too large (max 5MB)');
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, data.url]
      }));
    } catch (err) {
      console.error('Upload error:', err);
      setError(language === 'lv' ? 'Kļūda augšupielādējot' : 'Upload error');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        subcategory: formData.category === 'shop' ? (formData.subcategory || null) : null,
        product_type: formData.product_type || null,
        business_type: formData.category === 'business' ? (formData.business_type || null) : null,
        rental_type: formData.subcategory === 'rental' ? (formData.rental_type || null) : null,
        region: formData.region,
        price: formData.price ? parseFloat(formData.price) : null,
        price_type: formData.price_type,
        images: formData.images,
        contact_phone: formData.contact_phone || null,
        contact_email: formData.contact_email || null,
        website: formData.website || null,
        address: formData.address || null,
        event_date: formData.event_date ? new Date(formData.event_date).toISOString() : null,
        catalogs: formData.catalogs,
        collection_ids: formData.collection_ids || [],
        custom_subcategory: formData.subcategory === 'other' ? formData.custom_subcategory : null,
        custom_product_type: formData.product_type === 'other_food' || formData.product_type === 'other_material' || formData.product_type === 'other_handcraft' ? formData.custom_product_type : null,
        custom_business_type: formData.business_type === 'other_business' ? formData.custom_business_type : null,
        age_restriction: formData.age_restriction || null,
        event_subcategory: formData.event_subcategory || null,
        event_type: formData.event_type || null,
        service_subcategory: formData.service_subcategory || null,
        service_type: formData.service_type || null,
        service_area_type: formData.service_area_type || null,
        service_area_radius: formData.service_area_radius ? parseInt(formData.service_area_radius) : null,
        service_area_cities: formData.service_area_cities?.length > 0 ? formData.service_area_cities : null,
        service_area_region: formData.service_area_region || null,
        available_quantity: formData.available_quantity === '' ? null : parseInt(formData.available_quantity, 10),
        vat_rate: formData.vat_rate === '' ? null : parseInt(formData.vat_rate, 10),
        is_paused: !!formData.is_paused,
        location_id: formData.location_id || null,
        location_name: formData.location_name || null,
        location_district: formData.location_district || null,
        location_district_slug: formData.location_district_slug || null,
        lat: formData.lat ?? null,
        lng: formData.lng ?? null,
        custom_fields: customFields,
      };

      // Validate required custom fields (visible_if checked)
      for (const f of fieldSchema) {
        if (!f.required) continue;
        if (f.visible_if) {
          const cond = Object.entries(f.visible_if).every(([k, v]) => customFields[k] === v);
          if (!cond) continue;
        }
        const val = customFields[f.key];
        const missing = val === undefined || val === '' || val === null
          || (Array.isArray(val) && val.length === 0);
        if (missing) {
          setError((language === 'lv' ? 'Lūdzu aizpildi: ' : 'Please fill in: ') + (language === 'lv' ? f.label_lv : f.label_en));
          setLoading(false);
          return;
        }
      }

      if (isEditMode) {
        await axios.put(`${API}/listings/${id}`, payload);
      } else {
        await axios.post(`${API}/listings`, payload);
      }

      clearListingDraft();
      navigate('/my-listings');
    } catch (error) {
      console.error('Error saving listing:', error);
      setError(formatApiError(error, language === 'lv' ? 'Kļūda saglabājot' : 'Error saving listing'));
    } finally {
      setLoading(false);
    }
  };

  const categories = ['shop', 'service', 'event', 'project', 'business'];
  const priceTypes = [
    { value: 'fixed', label: t('fixed') },
    { value: 'hourly', label: t('hourly') },
    { value: 'negotiable', label: t('negotiable') },
    { value: 'free', label: t('free') }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="create-listing-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{language === 'lv' ? 'Atpakaļ' : 'Back'}</span>
        </button>

        <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8">
          <h1 className="font-outfit text-2xl font-bold text-[#121212] mb-6">
            {isEditMode
              ? (language === 'lv' ? 'Rediģēt sludinājumu' : 'Edit Listing')
              : t('createListing')}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="error-message">
                {error}
              </div>
            )}

            {draftRestored && !isEditMode && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between gap-3" data-testid="draft-restored-banner">
                <div className="flex items-center gap-2 text-sm text-amber-800">
                  <Save className="w-4 h-4 shrink-0" />
                  <span>{language === 'lv' ? 'Atjaunojām tavu nesaglabāto melnrakstu.' : 'We restored your unsaved draft.'}</span>
                </div>
                <button type="button" onClick={discardDraft} className="text-xs font-medium text-amber-700 hover:text-amber-900 underline shrink-0" data-testid="discard-draft-btn">
                  {language === 'lv' ? 'Sākt no jauna' : 'Start over'}
                </button>
              </div>
            )}

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-[#121212] font-medium">
                {t('title')} *
              </Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                placeholder={language === 'lv' ? 'Sludinājuma nosaukums' : 'Listing title'}
                data-testid="title-input"
              />
              <AiListingAssistant
                title={formData.title}
                image_url={formData.images?.[0] || formData.image_url}
                category_hint={formData.category}
                onApply={(suggestion) => {
                  setFormData(prev => ({
                    ...prev,
                    description: prev.description?.trim() ? prev.description : (suggestion.description || prev.description),
                    category: prev.category || suggestion.category || prev.category,
                    subcategory: prev.subcategory || suggestion.subcategory || prev.subcategory,
                    product_type: prev.product_type || suggestion.product_type || prev.product_type,
                    title: (suggestion.title_polished && !prev.title?.trim()) ? suggestion.title_polished : prev.title,
                  }));
                }}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-[#121212] font-medium">
                {t('description')} *
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={5}
                className="bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736] resize-none"
                placeholder={language === 'lv' ? 'Detalizēts apraksts...' : 'Detailed description...'}
                data-testid="description-input"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label className="text-[#121212] font-medium">
                {t('category')} *
              </Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                required
              >
                <SelectTrigger className="h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="category-select">
                  <SelectValue placeholder={t('category')} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {t(cat)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location — inherited from the shop profile (single source of truth) */}
            <div className="space-y-2" data-testid="listing-location-inherited">
              <Label className="text-[#121212] font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#D97736]" />
                {language === 'lv' ? 'Atrašanās vieta' : 'Location'}
              </Label>
              {addressSources.length > 1 && (
                <div className="flex flex-wrap gap-2" data-testid="listing-address-source">
                  {addressSources.map((src) => (
                    <button
                      key={src.key}
                      type="button"
                      onClick={() => applySource(src)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${activeSourceId === src.key ? 'bg-[#4A6B53] text-white border-[#4A6B53]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
                      data-testid={`address-source-${src.key}`}
                    >
                      {src.kind === 'shop' ? <Store className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
                      {src.label}
                    </button>
                  ))}
                </div>
              )}

              <LocationField
                value={{
                  region: formData.region,
                  location_name: formData.location_name,
                  location_region_slug: formData.location_region_slug,
                  location_district: formData.location_district,
                  location_district_slug: formData.location_district_slug,
                  lat: formData.lat,
                  lng: formData.lng,
                }}
                onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
                testid="listing-location-field"
              />

              {(formData.location_name || formData.region) && (
                <div className="rounded-xl border border-[#E5E4E0] bg-[#FAFAFA] p-4 space-y-2" data-testid="listing-precise-point">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#D97736] shrink-0" />
                    <p className="text-sm font-medium text-[#121212]">
                      {language === 'lv' ? 'Precīzs punkts kartē (ieteicams)' : language === 'ru' ? 'Точная точка на карте (рекомендуется)' : 'Precise point on map (recommended)'}
                    </p>
                  </div>
                  <p className="text-xs text-[#5C5C5C]">
                    {language === 'lv'
                      ? 'Uzklikšķini uz kartes vai velc marķieri, lai norādītu precīzu vietu. Tas padara attālumu pircējiem ļoti precīzu kārtošanā “Tuvākie vispirms”.'
                      : language === 'ru'
                      ? 'Нажмите на карту или перетащите маркер, чтобы указать точное место. Это делает расстояние для покупателей очень точным в сортировке «Сначала ближайшие».'
                      : 'Click the map or drag the marker to set the exact spot. This makes the distance shown to buyers very precise in the “Closest first” sort.'}
                  </p>
                  <MapPointPicker
                    lat={formData.lat ?? undefined}
                    lng={formData.lng ?? undefined}
                    onChange={(lat, lng) => setFormData((prev) => ({ ...prev, lat, lng }))}
                  />
                  {formData.lat != null && formData.lng != null && (
                    <p className="text-[11px] text-[#9CA3AF]" data-testid="listing-precise-coords">
                      {language === 'lv' ? 'Izvēlētais punkts: ' : language === 'ru' ? 'Выбранная точка: ' : 'Selected point: '}
                      {Number(formData.lat).toFixed(5)}, {Number(formData.lng).toFixed(5)}
                    </p>
                  )}
                </div>
              )}

              {!formData.region && (
                <div data-testid="listing-region-fallback">
                  <Label className="text-[#121212] font-medium text-sm">{t('region')} *</Label>
                  <Select
                    value={formData.region}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, region: value }))}
                  >
                    <SelectTrigger className="h-11 bg-white border-[#E5E4E0] mt-1" data-testid="region-select">
                      <SelectValue placeholder={t('region')} />
                    </SelectTrigger>
                    <SelectContent>
                      {regions.map((region) => (
                        <SelectItem key={region} value={region}>
                          {regionNames[region] || region}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <p className="text-xs text-[#5C5C5C]">
                {language === 'lv'
                  ? (addressSources.length > 1
                      ? 'Adrese tiek ņemta no izvēlētā avota (veikals vai biznesa lapa). Vari to rediģēt tikai šim sludinājumam — tas nemaina avota adresi.'
                      : 'Adrese pēc noklusējuma tiek ņemta no tava veikala/lapas. Vari to rediģēt tikai šim sludinājumam — tas nemaina veikala adresi.')
                  : 'The address is inherited from your shop / business page. You can edit it just for this listing \u2014 it won\u2019t change the source.'}
              </p>
            </div>

            {/* Service Area — for service/event categories */}
            {(formData.category === 'service' || formData.category === 'event') && (
              <div className="space-y-3 p-5 bg-[#F0F7F2] border border-[#4A6B53]/20 rounded-2xl">
                <Label className="text-[#121212] font-medium">
                  {language === 'lv' ? 'Pakalpojuma zona' : 'Service Area'}
                </Label>
                <Select
                  value={formData.service_area_type || ''}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, service_area_type: value, service_area_cities: [], service_area_radius: '', service_area_region: '' }))}
                >
                  <SelectTrigger className="h-11 bg-white border-[#E5E4E0]" data-testid="service-area-type">
                    <SelectValue placeholder={language === 'lv' ? 'Izvēlieties veidu' : 'Select type'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="radius">{language === 'lv' ? 'Apkalpes rādiuss (km)' : 'Radius (km)'}</SelectItem>
                    <SelectItem value="cities">{language === 'lv' ? 'Konkrētas pilsētas' : 'Specific cities'}</SelectItem>
                    <SelectItem value="region">{language === 'lv' ? 'Reģions' : 'Region'}</SelectItem>
                    <SelectItem value="online">{language === 'lv' ? 'Tiešsaistē (visā Latvijā)' : 'Online (nationwide)'}</SelectItem>
                  </SelectContent>
                </Select>

                {formData.service_area_type === 'online' && (
                  <p className="text-xs text-[#4A6B53]" data-testid="service-area-online-note">
                    {language === 'lv'
                      ? 'Šis pakalpojums tiek sniegts tiešsaistē — tas parādīsies sarakstā ar atzīmi "Tiešsaistē", bet ne kā punkts kartē.'
                      : 'This service is provided online — it will appear in listings with an "Online" badge, but not as a point on the map.'}
                  </p>
                )}

                {formData.service_area_type === 'radius' && (
                  <div className="space-y-2">
                    <Label className="text-sm text-[#5C5C5C]">{language === 'lv' ? 'Rādiuss (km)' : 'Radius (km)'}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="500"
                      value={formData.service_area_radius || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, service_area_radius: e.target.value }))}
                      placeholder="30"
                      className="h-11 bg-white border-[#E5E4E0]"
                      data-testid="service-area-radius"
                    />
                  </div>
                )}

                {formData.service_area_type === 'cities' && (
                  <div className="space-y-2">
                    <Label className="text-sm text-[#5C5C5C]">{language === 'lv' ? 'Izvēlieties pilsētas' : 'Select cities'}</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-48 overflow-y-auto p-2 bg-white rounded-xl border border-[#E5E4E0]">
                      {regions.map((r) => (
                        <label key={r} className="flex items-center gap-2 p-1.5 rounded hover:bg-[#F3F2EE] cursor-pointer text-xs">
                          <Checkbox
                            checked={(formData.service_area_cities || []).includes(r)}
                            onCheckedChange={(checked) => {
                              setFormData(prev => ({
                                ...prev,
                                service_area_cities: checked
                                  ? [...(prev.service_area_cities || []), r]
                                  : (prev.service_area_cities || []).filter(c => c !== r)
                              }));
                            }}
                            className="border-[#E5E4E0] data-[state=checked]:bg-[#4A6B53] data-[state=checked]:border-[#4A6B53]"
                          />
                          <span>{regionNames[r] || r}</span>
                        </label>
                      ))}
                    </div>
                    {(formData.service_area_cities || []).length > 0 && (
                      <p className="text-xs text-[#4A6B53]">{language === 'lv' ? `Izvēlētas: ${formData.service_area_cities.length} pilsētas` : `Selected: ${formData.service_area_cities.length} cities`}</p>
                    )}
                  </div>
                )}

                {formData.service_area_type === 'region' && (
                  <div className="space-y-2">
                    <Label className="text-sm text-[#5C5C5C]">{language === 'lv' ? 'Reģions' : 'Region'}</Label>
                    <Select
                      value={formData.service_area_region || ''}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, service_area_region: value }))}
                    >
                      <SelectTrigger className="h-11 bg-white border-[#E5E4E0]" data-testid="service-area-region">
                        <SelectValue placeholder={language === 'lv' ? 'Izvēlieties reģionu' : 'Select region'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kurzeme">Kurzeme</SelectItem>
                        <SelectItem value="vidzeme">Vidzeme</SelectItem>
                        <SelectItem value="latgale">Latgale</SelectItem>
                        <SelectItem value="zemgale">Zemgale</SelectItem>
                        <SelectItem value="pieriga">Pierīga</SelectItem>
                        <SelectItem value="riga_region">Rīgas reģions</SelectItem>
                        <SelectItem value="visa_latvija">{language === 'lv' ? 'Visa Latvija' : 'All Latvia'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* === EVENT TAXONOMY SUBCATEGORIES === */}
            {formData.category === 'event' && taxonomy?.event && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#121212] font-medium">{language === 'lv' ? 'Pasākuma veids' : 'Event Type'}</Label>
                  <Select
                    value={formData.event_subcategory}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, event_subcategory: v, event_type: '', age_restriction: '' }))}
                  >
                    <SelectTrigger className="h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="event-subcategory-select">
                      <SelectValue placeholder={language === 'lv' ? 'Izvēlieties veidu' : 'Select type'} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(taxonomy.event.subcategories).map(([key, sub]) => (
                        <SelectItem key={key} value={key}>{language === 'lv' ? sub.label_lv : sub.label_en}</SelectItem>
                      ))}
                      <SelectItem value="other">{language === 'lv' ? 'Cits...' : 'Other...'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.event_subcategory && formData.event_subcategory !== 'other' && taxonomy.event.subcategories[formData.event_subcategory]?.types && (
                  <div className="space-y-2">
                    <Label className="text-[#121212] font-medium">{language === 'lv' ? 'Precizēt' : 'Specify'}</Label>
                    <Select
                      value={formData.event_type}
                      onValueChange={(v) => {
                        const typeInfo = taxonomy.event.subcategories[formData.event_subcategory]?.types?.[v];
                        setFormData(prev => ({ ...prev, event_type: v, age_restriction: typeInfo?.age || '' }));
                      }}
                    >
                      <SelectTrigger className="h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="event-type-select">
                        <SelectValue placeholder={language === 'lv' ? 'Izvēlieties' : 'Select'} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(taxonomy.event.subcategories[formData.event_subcategory].types).map(([key, t]) => (
                          <SelectItem key={key} value={key}>
                            {language === 'lv' ? t.lv : t.en} {t.age && t.age !== '0+' ? `(${t.age})` : ''}
                          </SelectItem>
                        ))}
                        <SelectItem value="other">{language === 'lv' ? 'Cits...' : 'Other...'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* === SERVICE TAXONOMY SUBCATEGORIES === */}
            {formData.category === 'service' && taxonomy?.service && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#121212] font-medium">{language === 'lv' ? 'Pakalpojuma veids' : 'Service Type'}</Label>
                  <Select
                    value={formData.service_subcategory}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, service_subcategory: v, service_type: '', age_restriction: '' }))}
                  >
                    <SelectTrigger className="h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="service-subcategory-select">
                      <SelectValue placeholder={language === 'lv' ? 'Izvēlieties veidu' : 'Select type'} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(taxonomy.service.subcategories).map(([key, sub]) => (
                        <SelectItem key={key} value={key}>{language === 'lv' ? sub.label_lv : sub.label_en}</SelectItem>
                      ))}
                      <SelectItem value="other">{language === 'lv' ? 'Cits...' : 'Other...'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.service_subcategory && formData.service_subcategory !== 'other' && taxonomy.service.subcategories[formData.service_subcategory]?.types && (
                  <div className="space-y-2">
                    <Label className="text-[#121212] font-medium">{language === 'lv' ? 'Precizēt' : 'Specify'}</Label>
                    <Select
                      value={formData.service_type}
                      onValueChange={(v) => {
                        const typeInfo = taxonomy.service.subcategories[formData.service_subcategory]?.types?.[v];
                        setFormData(prev => ({ ...prev, service_type: v, age_restriction: typeInfo?.age || '' }));
                      }}
                    >
                      <SelectTrigger className="h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="service-type-select">
                        <SelectValue placeholder={language === 'lv' ? 'Izvēlieties' : 'Select'} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(taxonomy.service.subcategories[formData.service_subcategory].types).map(([key, t]) => (
                          <SelectItem key={key} value={key}>
                            {language === 'lv' ? t.lv : t.en} {t.age && t.age !== '0+' && t.age !== null ? `(${t.age})` : ''}
                          </SelectItem>
                        ))}
                        <SelectItem value="other">{language === 'lv' ? 'Cits...' : 'Other...'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Age Restriction (auto-filled from taxonomy or manual) */}
            {formData.age_restriction && formData.age_restriction !== '0+' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
                <span className="text-sm font-medium text-amber-700">{language === 'lv' ? 'Vecuma ierobežojums' : 'Age Restriction'}: {formData.age_restriction}</span>
              </div>
            )}

            {/* Subcategory for Shop */}
            {formData.category === 'shop' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#121212] font-medium">
                    {t('subcategory')}
                  </Label>
                  <Select
                    value={formData.subcategory}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, subcategory: value, rental_type: '' }))}
                  >
                    <SelectTrigger className="h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="subcategory-select">
                      <SelectValue placeholder={t('subcategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="food">{t('food')}</SelectItem>
                      <SelectItem value="materials">{t('materials')}</SelectItem>
                      <SelectItem value="handcraft">{t('handcraft')}</SelectItem>
                      <SelectItem value="rental">{t('rental')}</SelectItem>
                      <SelectItem value="other">{language === 'lv' ? 'Cits...' : 'Other...'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.subcategory === 'other' && (
                  <div className="space-y-2">
                    <Label className="text-[#121212] font-medium">{language === 'lv' ? 'Norādiet kategoriju' : 'Specify category'}</Label>
                    <Input
                      value={formData.custom_subcategory}
                      onChange={(e) => setFormData(prev => ({ ...prev, custom_subcategory: e.target.value }))}
                      placeholder={language === 'lv' ? 'Jūsu kategorija' : 'Your category'}
                      className="h-11 bg-[#FAFAFA] border-[#E5E4E0]"
                      data-testid="custom-subcategory-input"
                    />
                  </div>
                )}

                {formData.subcategory === 'rental' && (
                  <div className="space-y-2">
                    <Label className="text-[#121212] font-medium">
                      {language === 'lv' ? 'Īres veids' : 'Rental Type'}
                    </Label>
                    <Select
                      value={formData.rental_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, rental_type: value }))}
                    >
                      <SelectTrigger className="h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="rental-type-select">
                        <SelectValue placeholder={language === 'lv' ? 'Īres veids' : 'Rental type'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">{t('auto')}</SelectItem>
                        <SelectItem value="bicycle">{t('bicycle')}</SelectItem>
                        <SelectItem value="apartment">{t('apartment')}</SelectItem>
                        <SelectItem value="items">{t('items')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Product Type (3rd level) for shop subcategories */}
            {formData.category === 'shop' && formData.subcategory && formData.subcategory !== 'rental' && (
              <div className="space-y-2">
                <Label className="text-[#121212] font-medium">
                  {t('productType')}
                </Label>
                <Select
                  value={formData.product_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, product_type: value }))}
                >
                  <SelectTrigger className="h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="product-type-select">
                    <SelectValue placeholder={t('productType')} />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.subcategory === 'food' && (
                      <>
                        <SelectItem value="vegetables">{t('vegetables')}</SelectItem>
                        <SelectItem value="fruits">{t('fruits')}</SelectItem>
                        <SelectItem value="fish">{t('fish')}</SelectItem>
                        <SelectItem value="meat">{t('meat')}</SelectItem>
                        <SelectItem value="bread">{t('bread')}</SelectItem>
                        <SelectItem value="dairy">{t('dairy')}</SelectItem>
                        <SelectItem value="honey">{t('honey')}</SelectItem>
                        <SelectItem value="preserves">{t('preserves')}</SelectItem>
                        <SelectItem value="drinks">{t('drinks')}</SelectItem>
                        <SelectItem value="spices">{t('spices')}</SelectItem>
                        <SelectItem value="sweets">{t('sweets')}</SelectItem>
                        <SelectItem value="other_food">{t('other_food')}</SelectItem>
                      </>
                    )}
                    {formData.subcategory === 'materials' && (
                      <>
                        <SelectItem value="wood">{t('wood')}</SelectItem>
                        <SelectItem value="metal">{t('metal')}</SelectItem>
                        <SelectItem value="fabric">{t('fabric')}</SelectItem>
                        <SelectItem value="clay">{t('clay')}</SelectItem>
                        <SelectItem value="stone">{t('stone')}</SelectItem>
                        <SelectItem value="other_material">{t('other_material')}</SelectItem>
                      </>
                    )}
                    {formData.subcategory === 'handcraft' && (
                      <>
                        <SelectItem value="knitting">{t('knitting')}</SelectItem>
                        <SelectItem value="sewing">{t('sewing')}</SelectItem>
                        <SelectItem value="ceramics">{t('ceramics')}</SelectItem>
                        <SelectItem value="jewelry">{t('jewelry')}</SelectItem>
                        <SelectItem value="candles">{t('candles')}</SelectItem>
                        <SelectItem value="soap">{t('soap')}</SelectItem>
                        <SelectItem value="other_handcraft">{t('other_handcraft')}</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
                {(formData.product_type === 'other_food' || formData.product_type === 'other_material' || formData.product_type === 'other_handcraft') && (
                  <Input
                    value={formData.custom_product_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, custom_product_type: e.target.value }))}
                    placeholder={language === 'lv' ? 'Norādiet veidu' : 'Specify type'}
                    className="h-11 bg-[#FAFAFA] border-[#E5E4E0] mt-2"
                    data-testid="custom-product-type-input"
                  />
                )}
              </div>
            )}

            {/* Business Type for "Uzņēmumi" */}
            {formData.category === 'business' && (
              <div className="space-y-2">
                <Label className="text-[#121212] font-medium">
                  {t('businessType')}
                </Label>
                <Select
                  value={formData.business_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, business_type: value }))}
                >
                  <SelectTrigger className="h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="business-type-select">
                    <SelectValue placeholder={t('businessType')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sauna">{t('sauna')}</SelectItem>
                    <SelectItem value="kids_party">{t('kids_party')}</SelectItem>
                    <SelectItem value="escape_room">{t('escape_room')}</SelectItem>
                    <SelectItem value="spa">{t('spa')}</SelectItem>
                    <SelectItem value="restaurant">{t('restaurant')}</SelectItem>
                    <SelectItem value="cafe">{t('cafe')}</SelectItem>
                    <SelectItem value="workshop">{t('workshop')}</SelectItem>
                    <SelectItem value="tour">{t('tour')}</SelectItem>
                    <SelectItem value="sport">{t('sport')}</SelectItem>
                    <SelectItem value="entertainment">{t('entertainment')}</SelectItem>
                    <SelectItem value="other_business">{t('other_business')}</SelectItem>
                  </SelectContent>
                </Select>
                {formData.business_type === 'other_business' && (
                  <Input
                    value={formData.custom_business_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, custom_business_type: e.target.value }))}
                    placeholder={language === 'lv' ? 'Norādiet uzņēmuma veidu' : 'Specify business type'}
                    className="h-11 bg-[#FAFAFA] border-[#E5E4E0] mt-2"
                    data-testid="custom-business-type-input"
                  />
                )}
              </div>
            )}

            {/* Price */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price" className="text-[#121212] font-medium">
                  {t('price')} (€)
                </Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={handleChange}
                  className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                  placeholder="0.00"
                  data-testid="price-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#121212] font-medium">
                  {t('priceType')}
                </Label>
                <Select
                  value={formData.price_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, price_type: value }))}
                >
                  <SelectTrigger className="h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="price-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priceTypes.map((pt) => (
                      <SelectItem key={pt.value} value={pt.value}>
                        {pt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Inventory: available quantity + pause toggle */}
            <div className="bg-[#FAFAFA] border border-[#E5E4E0] rounded-xl p-4 space-y-4" data-testid="inventory-section">
              <div>
                <Label htmlFor="available_quantity" className="text-[#121212] font-medium">
                  {language === 'lv' ? 'Pieejamais daudzums' : 'Available quantity'}
                </Label>
                <p className="text-xs text-[#5C5C5C] mt-0.5 mb-2">
                  {language === 'lv'
                    ? 'Atstāj tukšu, ja krājumi ir bezgalīgi. Kad daudzums sasniegs 0, prece automātiski tiks pauzēta un paslēpta no publikas.'
                    : 'Leave empty for unlimited stock. When the quantity hits 0 the listing is automatically paused and hidden from public view.'}
                </p>
                <Input
                  id="available_quantity"
                  name="available_quantity"
                  type="number"
                  min="0"
                  step="1"
                  value={formData.available_quantity}
                  onChange={handleChange}
                  placeholder={language === 'lv' ? 'Bezgalīgs (atstāj tukšu)' : 'Unlimited (leave empty)'}
                  className="h-11 bg-white border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                  data-testid="available-quantity-input"
                />
              </div>

              {user?.seller_legal_info?.vat_number && (
                <div data-testid="vat-rate-field">
                  <Label htmlFor="vat_rate" className="text-[#121212] font-medium">
                    {language === 'lv' ? 'PVN likme' : 'VAT rate'}
                  </Label>
                  <p className="text-xs text-[#5C5C5C] mt-0.5 mb-2">
                    {language === 'lv'
                      ? 'Šai precei piemērojamā PVN likme. Atstāj tukšu, lai izmantotu profila noklusējuma likmi. (21% standarta; 12% pārtika/medikamenti/grāmatas; 5% noteiktas grāmatas; 0% eksports.)'
                      : 'VAT rate for this product. Leave empty to use your profile default. (21% standard; 12% food/meds/books; 5% certain books; 0% export.)'}
                  </p>
                  <select
                    id="vat_rate"
                    name="vat_rate"
                    value={formData.vat_rate}
                    onChange={handleChange}
                    className="h-11 w-full rounded-md bg-white border border-[#E5E4E0] px-3 text-sm focus:border-[#D97736] focus:outline-none"
                    data-testid="vat-rate-select"
                  >
                    <option value="">{language === 'lv' ? 'Noklusējuma (profils)' : 'Default (profile)'}</option>
                    <option value="21">21% {language === 'lv' ? '(standarta)' : '(standard)'}</option>
                    <option value="12">12% {language === 'lv' ? '(pārtika, medikamenti, grāmatas)' : '(food, meds, books)'}</option>
                    <option value="5">5% {language === 'lv' ? '(noteiktas grāmatas)' : '(certain books)'}</option>
                    <option value="0">0% {language === 'lv' ? '(eksports)' : '(export)'}</option>
                  </select>
                </div>
              )}
              <label className="flex items-start gap-3 cursor-pointer" data-testid="is-paused-toggle">
                <Checkbox
                  checked={formData.is_paused}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_paused: !!checked }))}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-medium text-[#121212]">
                    {language === 'lv' ? 'Pauzēt sludinājumu' : 'Pause listing'}
                  </div>
                  <div className="text-xs text-[#5C5C5C]">
                    {language === 'lv'
                      ? 'Pauzētie sludinājumi nav redzami publikai, bet paliek saglabāti.'
                      : 'Paused listings are hidden from the public but remain saved.'}
                  </div>
                </div>
              </label>
            </div>

            {/* Event Date (only for events) */}
            {formData.category === 'event' && (
              <div className="space-y-2">
                <Label htmlFor="event_date" className="text-[#121212] font-medium">
                  {t('eventDate')}
                </Label>
                <Input
                  id="event_date"
                  name="event_date"
                  type="datetime-local"
                  value={formData.event_date}
                  onChange={handleChange}
                  className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                  data-testid="event-date-input"
                />
              </div>
            )}

            {/* Shop Collections — seller-defined groups (e.g. "Lapenes", "Pirtis").
                Multi-select. Sellers can create new collections inline. */}
            <ShopCollectionsSelector
              language={language}
              collections={shopCollections}
              selectedIds={formData.collection_ids}
              onChange={(ids) => setFormData(prev => ({ ...prev, collection_ids: ids }))}
              onCreate={createCollectionInline}
            />

            {/* Seasonal Catalogs — only the seller's PAID catalogs are selectable.
                Sellers without paid catalogs see a CTA banner inviting them to apply. */}
            {catalogInfo && (() => {
              const allSeasonCatalogs = Object.values(catalogInfo.seasons || {})
                .flatMap(s => s?.catalogs || []);
              const extraCatalogs = catalogInfo.extra_catalogs || [];
              const allValid = new Set([...allSeasonCatalogs, ...extraCatalogs]);
              const catNames = catalogInfo.catalog_names?.[language] || catalogInfo.catalog_names?.lv || {};
              const extraNames = catalogInfo.extra_catalog_names?.[language] || catalogInfo.extra_catalog_names?.lv || {};
              const nameFor = (id) => catNames[id] || extraNames[id] || id;
              const myCatalogs = paidCatalogs.filter(c => allValid.has(c));

              const toggleCatalog = (catId) => {
                setFormData(prev => ({
                  ...prev,
                  catalogs: prev.catalogs.includes(catId)
                    ? prev.catalogs.filter(c => c !== catId)
                    : [...prev.catalogs, catId]
                }));
              };

              // Not verified → can't place in catalogs yet. Prompt verification.
              if (myCatalogs.length === 0) {
                return (
                  <div className="p-5 bg-[#FFF8F0] border border-[#D97736]/20 rounded-2xl space-y-3" data-testid="catalog-empty-banner">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-[#D97736]" />
                      <Label className="text-[#121212] font-medium">
                        {language === 'lv' ? 'Katalogi' : 'Catalogs'}
                      </Label>
                    </div>
                    <p className="text-sm text-[#5C5C5C]">
                      {language === 'lv'
                        ? 'Katalogi ir BEZMAKSAS verificētiem veikaliem. Verificē savu veikalu, lai ievietotu sludinājumus sezonas un tematiskajos katalogos.'
                        : 'Catalogs are FREE for verified shops. Verify your shop to place listings in seasonal and themed catalogs.'}
                    </p>
                    <Button
                      type="button"
                      onClick={() => navigate('/shop-onboarding')}
                      className="bg-[#D97736] hover:bg-[#C06528] text-white"
                      data-testid="catalog-verify-cta"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      {language === 'lv' ? 'Verificēt veikalu' : 'Verify shop'}
                    </Button>
                  </div>
                );
              }

              const useDropdown = myCatalogs.length > 5;

              return (
                <div className="space-y-3 p-5 bg-[#FFF8F0] border border-[#D97736]/20 rounded-2xl" data-testid="catalog-selector">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <BookOpen className="w-5 h-5 text-[#D97736]" />
                    <Label className="text-[#121212] font-medium">
                      {language === 'lv' ? 'Ievietot katalogos' : 'Place in catalogs'}
                    </Label>
                    <span className="text-xs text-white bg-[#4A6B53] px-2 py-0.5 rounded-full">
                      {language === 'lv' ? 'Bezmaksas' : 'Free'}
                    </span>
                  </div>
                  <p className="text-xs text-[#5C5C5C] mb-2">
                    {language === 'lv'
                      ? 'Atzīmējiet katalogus, kuros vēlaties ievietot šo preci/pakalpojumu'
                      : 'Select catalogs where you want this product/service to appear'}
                  </p>

                  {useDropdown ? (
                    // Compact summary + popover-like inline panel for many catalogs.
                    <details className="rounded-xl border border-[#E5E4E0] bg-white">
                      <summary className="px-4 py-3 cursor-pointer text-sm flex items-center justify-between list-none" data-testid="catalog-dropdown-summary">
                        <span className="text-[#121212]">
                          {formData.catalogs.length === 0
                            ? (language === 'lv' ? 'Izvēlēties katalogus...' : 'Select catalogs...')
                            : `${language === 'lv' ? 'Izvēlēti' : 'Selected'}: ${formData.catalogs.length}`}
                        </span>
                        <span className="text-[#D97736] text-xs">▾</span>
                      </summary>
                      <div className="max-h-72 overflow-y-auto border-t border-[#E5E4E0] p-2">
                        {myCatalogs.map(catId => (
                          <label key={catId} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-[#FFF8F0] cursor-pointer transition-colors">
                            <Checkbox
                              checked={formData.catalogs.includes(catId)}
                              onCheckedChange={() => toggleCatalog(catId)}
                              className="border-[#E5E4E0] data-[state=checked]:bg-[#D97736] data-[state=checked]:border-[#D97736]"
                              data-testid={`catalog-${catId}`}
                            />
                            <span className="text-sm text-[#121212]">{nameFor(catId)}</span>
                          </label>
                        ))}
                      </div>
                    </details>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {myCatalogs.map(catId => (
                        <label
                          key={catId}
                          className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-white cursor-pointer transition-colors"
                        >
                          <Checkbox
                            checked={formData.catalogs.includes(catId)}
                            onCheckedChange={() => toggleCatalog(catId)}
                            className="border-[#E5E4E0] data-[state=checked]:bg-[#D97736] data-[state=checked]:border-[#D97736]"
                            data-testid={`catalog-${catId}`}
                          />
                          <span className="text-sm text-[#121212]">{nameFor(catId)}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {formData.catalogs.length > 0 && (
                    <p className="text-xs text-[#D97736] font-medium mt-1">
                      {language === 'lv'
                        ? `Izvēlēti: ${formData.catalogs.length} katalogi`
                        : `Selected: ${formData.catalogs.length} catalogs`}
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Images */}
            <div className="space-y-2">
              <Label className="text-[#121212] font-medium">
                {t('images')} <span className="text-xs text-[#5C5C5C] font-normal">({formData.images.length}/{imageLimit})</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  type="url"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                  placeholder="https://example.com/image.jpg"
                  data-testid="image-url-input"
                />
                <Button
                  type="button"
                  onClick={handleAddImage}
                  variant="outline"
                  className="h-11 border-[#D97736] text-[#D97736] hover:bg-[#D97736] hover:text-white"
                  data-testid="add-image-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {/* File Upload */}
              <div className="flex items-center gap-3 mt-2">
                <label className="flex items-center gap-2 px-4 py-2 bg-[#F3F2EE] rounded-xl cursor-pointer hover:bg-[#E5E4E0] transition-colors text-sm text-[#5C5C5C]">
                  <Upload className="w-4 h-4" />
                  {uploadingImage
                    ? (language === 'lv' ? 'Augšupielādē...' : 'Uploading...')
                    : (language === 'lv' ? 'Augšupielādēt failu' : 'Upload file')}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploadingImage}
                    data-testid="image-file-input"
                  />
                </label>
                <span className="text-xs text-[#5C5C5C]">Max 5MB</span>
              </div>
              {formData.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {formData.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={resolveAssetUrl(img)}
                        alt=""
                        className="w-20 h-20 object-cover rounded-lg border border-[#E5E4E0]"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(idx)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-image-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Contact info moved to shop profile — removed from listing form */}

            {/* Website & Address */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="website" className="text-[#121212] font-medium">
                  {t('website')}
                </Label>
                <Input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleChange}
                  className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                  placeholder="https://www.example.com"
                  data-testid="website-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="text-[#121212] font-medium">
                  {t('address')}
                </Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                  placeholder={language === 'lv' ? 'Iela 1, Pilsēta' : 'Street 1, City'}
                  data-testid="address-input"
                />
              </div>
            </div>

            {/* Category-specific (custom) fields */}
            <CustomFieldsRenderer
              fields={fieldSchema}
              values={customFields}
              onChange={(k, v) => setCustomFields((prev) => ({ ...prev, [k]: v }))}
            />

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1 h-11 border-[#E5E4E0] text-[#5C5C5C] hover:text-[#121212]"
                data-testid="cancel-btn"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-11 bg-[#D97736] hover:bg-[#C06528] text-white"
                data-testid="save-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('loading')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    {t('save')}
                  </span>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}


function ShopCollectionsSelector({ language, collections, selectedIds, onChange, onCreate }) {
  const isLV = language === 'lv';
  const [creatingName, setCreatingName] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const sorted = [...(collections || [])].sort((a, b) => a.display_order - b.display_order);

  const toggle = (id) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter(c => c !== id) : [...selectedIds, id]);
  };

  const handleCreate = async () => {
    if (!creatingName.trim() || creatingName.trim().length < 2) return;
    setBusy(true);
    const created = await onCreate(creatingName);
    setBusy(false);
    if (created) {
      onChange([...selectedIds, created.id]);
      setCreatingName('');
      setShowCreate(false);
    }
  };

  return (
    <div className="space-y-3 p-5 bg-white border border-[#E5E4E0] rounded-2xl" data-testid="collections-selector">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <FolderTree className="w-5 h-5 text-[#D97736]" />
          <Label className="text-[#121212] font-medium">
            {isLV ? 'Veikala kolekcija' : 'Shop collection'}
          </Label>
          <span className="text-xs text-[#8A8A8A]">
            ({isLV ? 'neobligāts, vairākas' : 'optional, multiple'})
          </span>
        </div>
        <button
          type="button"
          onClick={() => navigate('/shop-collections')}
          className="text-xs text-[#D97736] hover:underline"
          data-testid="manage-collections-from-form"
        >
          {isLV ? 'Pārvaldīt →' : 'Manage →'}
        </button>
      </div>
      <p className="text-xs text-[#5C5C5C]">
        {isLV
          ? 'Sagrupē sludinājumu savā veikalā (piem., "Lapenes", "Pirtis"). Pircēji to redzēs kā filtru tavā veikala lapā.'
          : 'Group this listing within your shop (e.g., "Gazebos", "Saunas"). Buyers will see this as a filter on your shop page.'}
      </p>

      {sorted.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {sorted.map(c => {
            const active = selectedIds.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggle(c.id)}
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                  active
                    ? 'bg-[#D97736] text-white border-[#D97736]'
                    : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#D97736]'
                }`}
                data-testid={`collection-pill-${c.id}`}
              >
                {c.name}
              </button>
            );
          })}
        </div>
      )}

      {showCreate ? (
        <div className="flex gap-2 items-start">
          <Input
            value={creatingName}
            onChange={e => setCreatingName(e.target.value)}
            placeholder={isLV ? 'piem., Lapenes' : 'e.g., Gazebos'}
            maxLength={60}
            className="bg-[#FAFAFA] border-[#E5E4E0]"
            data-testid="new-collection-name"
          />
          <Button type="button" size="sm" disabled={busy || creatingName.trim().length < 2} onClick={handleCreate} className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="new-collection-save">
            {busy ? '...' : (isLV ? 'Pievienot' : 'Add')}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => { setShowCreate(false); setCreatingName(''); }}>
            {isLV ? 'Atcelt' : 'Cancel'}
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="text-sm text-[#D97736] hover:underline flex items-center gap-1"
          data-testid="add-collection-inline"
        >
          <Plus className="w-3.5 h-3.5" />
          {isLV ? 'Izveidot jaunu kolekciju' : 'Create new collection'}
        </button>
      )}
    </div>
  );
}
